import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, IconButton, Button, Tooltip, Chip, CircularProgress } from '@mui/material';
import { Close, Delete, InsertDriveFile, Upload } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import MarkdownEditor from './MarkdownEditor';
import MarkdownDisplay from './MarkdownDisplay';
import { contentToPlainText, isLexicalJson } from '../utils/lexicalContent';
import AttachmentImage from './AttachmentImage';
import ImageViewer from './ImageViewer';
import FilePondUploader from './FilePondUploader';
import ConfirmDialog from './admin/ConfirmDialog';
import CommentThread from './CommentThread';
import BugherdCardPanel, { withProxiedCommentImages, withProxiedDescriptionImages } from './BugherdCardPanel';
import { isRasterImage, restoreAttachments } from '../utils/uploadUtils';
import { addBoardCard, updateBoardCard, detachBoardCardAttachment, getBoardCards, retryBoardCardPush } from '../redux/ducks/task';
import { showAlert } from '../redux/slices/alerts';
import { formatBytes } from '../utils/navigateUtils';
import { getApiUrl } from '../config';
import { useHasPermission } from '../utils/permissionUtils';
import { ARCHIVED_COLUMN_ID, STALE_COLUMN_MESSAGE, isStaleColumnError, isMissingCardError } from '../utils/kanbanColumns';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: theme.shape.borderRadius,
    },
}));

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingRight: theme.spacing(5),
}));

const DialogHeading = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
}));

const DialogHelper = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const DialogWarning = styled(DialogHelper)(({ theme }) => ({
    color: theme.palette.warning.main,
}));

const CloseBtn = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
}));

const Section = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
}));

const ImageGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
}));

const ImageTile = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: 160,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
    transition: 'border-color 120ms ease, transform 120ms ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
    '&:hover .kanban-image-delete': {
        opacity: 1,
    },
}));

const TileChip = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: theme.spacing(0.5),
    bottom: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
}));

const ImageDeleteButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    padding: 2,
    opacity: 0,
    transition: 'opacity 120ms ease',
    backgroundColor: 'rgba(255,255,255,0.9)',
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
}));

const FileRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.75),
    backgroundColor: theme.palette.background.paper,
}));

const FileRowName = styled(Box)(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    '&:hover .file-row-name-text': {
        color: theme.palette.primary.dark,
        textDecoration: 'underline',
    },
}));

const FileRowNameText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const DescriptionViewer = styled(Box, { shouldForwardProp: (prop) => prop !== '$clickable' })(({ theme, $clickable }) => ({
    border: `1px solid transparent`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 1),
    minHeight: theme.spacing(6),
    cursor: $clickable ? 'text' : 'default',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    ...($clickable && {
        '&:hover': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.action.hover,
        },
    }),
}));

const PushStateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const SendToBoardButton = styled(IconButton)(({ theme }) => ({
    padding: theme.spacing(0.25),
    color: theme.palette.warning.main,
}));

const SendToBoard = ({ label, busy, onClick }) => (
    <Tooltip title={label}>
        <span>
            <SendToBoardButton size="small" aria-label={label} disabled={busy} onClick={onClick}>
                {busy ? <CircularProgress size={14} /> : <Upload fontSize="small" />}
            </SendToBoardButton>
        </span>
    </Tooltip>
);

const NotOnBoardChip = () => (
    <Tooltip title="Pollux could not put this file on the connected BugHerd board, so the client cannot see it there.">
        <Chip variant="outlined" size="small" color="warning" label="Not on board" />
    </Tooltip>
);

const pushStateFields = (fields) => {
    const labels = (Array.isArray(fields) ? fields : []).filter((field) => field === 'name' || field === 'description');
    if (labels.length === 0) return null;
    return labels.length === 2 ? 'name and description' : labels[0];
};

const KanbanCardDialog = ({ open, taskId, item, columnId, canEdit: boardCanEdit = false, isLocked = false, onClose, onCardsReplaced, onCardCommentsChanged, onStaleColumn, onMissingCard, onCardPushState, onCommentPushState }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const currentUserId = useSelector((state) => state.auth.userId);
    const isCreating = !item || !item._id;

    const originalName = item?.name || '';
    const originalDescription = item?.description || '';

    const creatorId = item?.createdBy && (item.createdBy._id || item.createdBy);
    const isCreator = !!creatorId && !!currentUserId && String(creatorId) === String(currentUserId);
    const canEditQATask = hasPermission('editQATask');
    const canEdit = boardCanEdit && (isCreating || isCreator || canEditQATask);
    const isMirrored = !isCreating && !!item?.externalId;
    // The Archived column mirrors tasks archived in BugHerd, so its text is read-only here for the same reason its cards cannot be moved.
    const canEditFields = canEdit && !(isMirrored && item?.columnId === ARCHIVED_COLUMN_ID);

    const [nameDraft, setNameDraft] = useState(originalName);
    const [descriptionDraft, setDescriptionDraft] = useState(originalDescription);
    const [descriptionEditing, setDescriptionEditing] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [descriptionKey, setDescriptionKey] = useState(0);
    const [confirmDetachId, setConfirmDetachId] = useState(null);
    const [viewerAttachment, setViewerAttachment] = useState(null);
    const [sendingToBoard, setSendingToBoard] = useState(null);

    useEffect(() => {
        setNameDraft(originalName);
        setDescriptionDraft(originalDescription);
        setDescriptionEditing(false);
        setPendingFiles([]);
        setIsSaving(false);
        setDescriptionKey((k) => k + 1);
    }, [item?._id, open, originalName, originalDescription]);

    // The proxy authorises the read against the board's task, so an imported body's image srcs can only be completed where taskId is known.
    const comments = useMemo(() => withProxiedCommentImages(item?.comments, taskId, item?._id), [item, taskId]);

    // Only what is shown is proxied: the draft below is what gets saved and sent back to the board, and it stays as written.
    const descriptionPreview = useMemo(() => withProxiedDescriptionImages(descriptionDraft, taskId, item?._id), [descriptionDraft, taskId, item?._id]);

    const attachments = useMemo(() => Array.isArray(item?.attachments) ? item.attachments.filter((a) => a && typeof a === 'object' && !a.isDeleted) : [], [item]);
    const imageAttachments = useMemo(() => attachments.filter((a) => isRasterImage(a?.mimeType)), [attachments]);
    const fileAttachments = useMemo(() => attachments.filter((a) => !isRasterImage(a?.mimeType)), [attachments]);
    const attachmentsNotOnBoard = useMemo(() => new Set((item?.externalAttachmentPushFailed || []).map(String)), [item]);

    const trimmedName = (nameDraft || '').trim();
    const nameDirty = canEditFields && trimmedName !== originalName.trim();
    // Opening the editor rewrites a synced card's plain text as a Lexical state with nobody typing, so an edit is the text changing, not the blob.
    const draftText = useMemo(() => contentToPlainText(descriptionDraft), [descriptionDraft]);
    const originalText = useMemo(() => contentToPlainText(originalDescription), [originalDescription]);
    const formattingDirty = !isMirrored && isLexicalJson(originalDescription) && (descriptionDraft || '') !== originalDescription;
    const descriptionDirty = canEditFields && (draftText !== originalText || formattingDirty);
    // Only for a description already held as an editor state: one still holding the board's plain text is reshaped by the editor on open, which is nobody's change.
    const formattingIsDropped = canEditFields && isMirrored && isLexicalJson(originalDescription)
        && (descriptionDraft || '') !== originalDescription && draftText === originalText;
    // A widget-filed BugHerd task has no title, so its name is derived from the description and showing both would print the same line twice.
    const descriptionRepeatsName = isMirrored && !canEditFields && originalDescription.trim() === originalName.trim();
    const hasPendingFiles = pendingFiles.length > 0;
    const isDirty = isCreating || nameDirty || descriptionDirty || hasPendingFiles;

    const handleDownloadFile = useCallback((attachment) => {
        if (!attachment?._id) return;
        const url = `${getApiUrl()}/attachment/${attachment._id}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.name || 'file';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const buildFreshFileKeys = useCallback(() => {
        if (!hasPendingFiles) return [];
        const ready = pendingFiles.filter((f) => {
            try {
                return !!f.serverId || (f.file && (f.file.id || f.serverId));
            } catch {
                return false;
            }
        });
        if (ready.length === 0) return [];
        const fileKeys = restoreAttachments(ready, []);
        return fileKeys.filter((k) => !k.id && k.storageKey);
    }, [pendingFiles, hasPendingFiles]);

    const handleAttachmentReupload = useCallback(() => {
        if (!taskId) return;
        dispatch(getBoardCards(taskId, (data, error) => {
            if (error || !data?.cards) return;
            if (typeof onCardsReplaced === 'function') onCardsReplaced(data.cards);
        }));
    }, [dispatch, taskId, onCardsReplaced]);

    const handleDetach = useCallback((attachmentId) => {
        if (!canEdit || !taskId || !item?._id) return;
        dispatch(detachBoardCardAttachment(taskId, item._id, attachmentId, (data, error) => {
            if (error || !data?.cards) {
                if (isMissingCardError(error) && typeof onMissingCard === 'function') onMissingCard();
                return;
            }
            if (typeof onCardsReplaced === 'function') onCardsReplaced(data.cards);
        }));
        setConfirmDetachId(null);
    }, [canEdit, taskId, item, dispatch, onCardsReplaced, onMissingCard]);

    const handleSendToBoard = useCallback((key, payload) => {
        if (!taskId || !item?._id || sendingToBoard) return;
        setSendingToBoard(key);
        dispatch(retryBoardCardPush(taskId, item._id, payload, (data, error) => {
            setSendingToBoard(null);
            if (error || !data) return;
            if (data.card && typeof onCardPushState === 'function') onCardPushState(item._id, data.card);
            if (data.comment && typeof onCommentPushState === 'function') onCommentPushState(item._id, data.comment);
        }));
    }, [dispatch, taskId, item, sendingToBoard, onCardPushState, onCommentPushState]);

    const closeAndReset = useCallback(() => {
        setPendingFiles([]);
        setIsSaving(false);
        if (typeof onClose === 'function') onClose();
    }, [onClose]);

    const handleCancel = useCallback(() => {
        closeAndReset();
    }, [closeAndReset]);

    const handlePrimary = useCallback(() => {
        if (!canEdit) {
            closeAndReset();
            return;
        }

        if (isCreating) {
            if (!trimmedName) return;
            if (isUploading || isSaving) return;
            const freshFileKeys = buildFreshFileKeys();
            const payload = {
                name: trimmedName,
                description: descriptionDraft || '',
            };
            if (columnId) {
                payload.columnId = columnId;
            }
            if (freshFileKeys.length > 0) {
                payload.fileKeys = freshFileKeys;
            }
            setIsSaving(true);
            dispatch(addBoardCard(taskId, payload, (data, error) => {
                if (error || !data?.cards) {
                    setIsSaving(false);
                    if (isStaleColumnError(error)) {
                        dispatch(showAlert({ severity: 'warning', message: STALE_COLUMN_MESSAGE }));
                        if (typeof onStaleColumn === 'function') onStaleColumn();
                    }
                    return;
                }
                if (typeof onCardsReplaced === 'function') onCardsReplaced(data.cards);
                closeAndReset();
            }));
            return;
        }

        if (!isDirty) {
            closeAndReset();
            return;
        }

        if (isUploading || isSaving) return;

        const fields = {};
        if (nameDirty) {
            if (!trimmedName) {
                dispatch(showAlert({ severity: 'warning', message: 'Card name cannot be empty' }));
                return;
            }
            fields.name = trimmedName;
        }
        if (descriptionDirty) {
            fields.description = descriptionDraft || '';
        }
        if (hasPendingFiles) {
            const freshFileKeys = buildFreshFileKeys();
            if (freshFileKeys.length > 0) {
                fields.fileKeys = freshFileKeys;
            }
        }

        if (Object.keys(fields).length === 0) {
            closeAndReset();
            return;
        }

        setIsSaving(true);
        dispatch(updateBoardCard(taskId, item._id, fields, (data, error) => {
            if (error || !data?.cards) {
                setIsSaving(false);
                if (isMissingCardError(error) && typeof onMissingCard === 'function') onMissingCard();
                return;
            }
            if (typeof onCardsReplaced === 'function') onCardsReplaced(data.cards);
            closeAndReset();
        }));
    }, [canEdit, isCreating, isDirty, isUploading, isSaving, trimmedName, descriptionDraft, descriptionDirty, nameDirty, hasPendingFiles, buildFreshFileKeys, columnId, dispatch, taskId, item, onCardsReplaced, onStaleColumn, closeAndReset, onMissingCard]);

    let primaryLabel;
    let primaryDisabled;
    if (isCreating) {
        primaryLabel = isSaving ? 'Creating…' : 'Create';
        primaryDisabled = !trimmedName || isUploading || isSaving;
    } else if (!canEdit) {
        primaryLabel = 'Close';
        primaryDisabled = false;
    } else if (isDirty) {
        primaryLabel = isSaving ? 'Saving…' : 'Save & Close';
        primaryDisabled = isUploading || isSaving || (nameDirty && !trimmedName);
    } else {
        primaryLabel = 'Close';
        primaryDisabled = false;
    }

    const titleText = isCreating ? 'New card' : (item?.name || 'Untitled');

    const renderPushState = () => {
        if (!isMirrored) return null;
        const failed = pushStateFields(item?.externalPushFailed);
        const truncated = pushStateFields(item?.externalPushTruncated);
        const canSend = canEditFields && !isLocked;
        const sendText = (
            <SendToBoard
                label="Send this card's text to the board again"
                busy={sendingToBoard === 'text'}
                onClick={() => handleSendToBoard('text', { target: 'text' })}
            />
        );
        if (failed) {
            return (
                <PushStateBox>
                    <Tooltip title={`Pollux could not confirm the ${failed} of this card reached the connected BugHerd board, so the client may still be looking at the old text.`}>
                        <Chip variant="outlined" size="small" color="warning" label="Not on board" />
                    </Tooltip>
                    {canSend && sendText}
                </PushStateBox>
            );
        }
        if (item?.externalImagePushFailedAt) {
            return (
                <PushStateBox>
                    <Tooltip title="A picture in this description could not be uploaded to the connected BugHerd board, so the client sees a note where it should be.">
                        <Chip variant="outlined" size="small" color="warning" label="Image not on board" />
                    </Tooltip>
                    {canSend && sendText}
                </PushStateBox>
            );
        }
        if (truncated) {
            return (
                <Tooltip title={`BugHerd limits how long a card's title and description can be, so the ${truncated} on the board is shorter than the full text kept here.`}>
                    <Chip variant="outlined" size="small" color="info" label="Shortened on board" />
                </Tooltip>
            );
        }
        return null;
    };

    return (
        <StyledDialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
            <DialogTitle>
                {isCreating && <DialogHeading>Add card</DialogHeading>}
                {isCreating && (
                    <DialogHelper variant="caption" display="block">
                        Name the card, then add a description, attachments, or comments after you create it.
                    </DialogHelper>
                )}
                <HeaderRow>
                    {canEditFields ? (
                        <TextField
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            placeholder={isCreating ? 'Card name' : ''}
                            variant="standard"
                            fullWidth
                            autoFocus={isCreating}
                            inputProps={{ style: { fontSize: '1.2rem', fontWeight: 600 } }}
                        />
                    ) : (
                        <Typography variant="h6">{titleText}</Typography>
                    )}
                    {renderPushState()}
                </HeaderRow>
                <CloseBtn onClick={handleCancel} aria-label="close">
                    <Close />
                </CloseBtn>
            </DialogTitle>
            <DialogContent dividers>
                {!descriptionRepeatsName && (
                <Section>
                    <SectionLabel>Description</SectionLabel>
                    {isMirrored && canEditFields && (
                        <DialogHelper variant="caption" display="block">
                            The name and description of a mirrored card belong to the connected board — an edit here is sent there, so the client sees it.
                        </DialogHelper>
                    )}
                    {formattingIsDropped && (
                        <DialogWarning variant="caption" display="block">
                            The board takes the words only, so a change to the formatting alone is not kept.
                        </DialogWarning>
                    )}
                    {canEditFields && descriptionEditing ? (
                        <MarkdownEditor
                            key={descriptionKey}
                            initialContent={descriptionDraft}
                            onChange={(content) => setDescriptionDraft(content || '')}
                            placeholder="Add a description"
                            ignoreSelectionChange
                            allowHtml={false}
                        />
                    ) : (
                        <DescriptionViewer onClick={canEditFields ? () => setDescriptionEditing(true) : undefined} $clickable={canEditFields}>
                            {descriptionDraft ? (
                                <MarkdownDisplay content={descriptionPreview} />
                            ) : (
                                <Typography variant="body2" color="textSecondary" fontStyle="italic">
                                    {canEditFields ? 'Click to add a description' : 'No description'}
                                </Typography>
                            )}
                        </DescriptionViewer>
                    )}
                </Section>
                )}

                {isMirrored && <BugherdCardPanel externalData={item?.externalData} taskId={taskId} cardId={item?._id} onViewImage={setViewerAttachment} />}

                <Section>
                    <SectionLabel>Attachments</SectionLabel>
                    {!isCreating && attachments.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            {canEdit ? 'No attachments yet — upload a file below to add one' : 'No attachments yet'}
                        </Typography>
                    )}
                    {imageAttachments.length > 0 && (
                        <ImageGrid>
                            {imageAttachments.map((attachment) => (
                                <ImageTile key={attachment._id} onClick={() => setViewerAttachment(attachment)}>
                                    <AttachmentImage attachment={attachment} alt={attachment.name} />
                                    {isMirrored && attachmentsNotOnBoard.has(String(attachment._id)) && (
                                        <TileChip onClick={(e) => e.stopPropagation()}>
                                            <PushStateBox>
                                                <NotOnBoardChip />
                                                {canEdit && !isLocked && (
                                                    <SendToBoard
                                                        label="Send this file to the board again"
                                                        busy={sendingToBoard === attachment._id}
                                                        onClick={() => handleSendToBoard(attachment._id, { target: 'attachments', attachmentIds: [attachment._id] })}
                                                    />
                                                )}
                                            </PushStateBox>
                                        </TileChip>
                                    )}
                                    {canEdit && (
                                        <Tooltip title="Remove attachment">
                                            <ImageDeleteButton
                                                className="kanban-image-delete"
                                                size="small"
                                                aria-label="remove attachment"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmDetachId(attachment._id);
                                                }}
                                            >
                                                <Close fontSize="small" />
                                            </ImageDeleteButton>
                                        </Tooltip>
                                    )}
                                </ImageTile>
                            ))}
                        </ImageGrid>
                    )}
                    {fileAttachments.map((attachment) => (
                        <FileRow key={attachment._id}>
                            <InsertDriveFile color="action" />
                            <FileRowName onClick={() => handleDownloadFile(attachment)}>
                                <FileRowNameText className="file-row-name-text" variant="body2">
                                    {attachment.name}
                                </FileRowNameText>
                                {typeof attachment.size === 'number' && (
                                    <Typography variant="caption" color="textSecondary">
                                        {formatBytes(attachment.size)}
                                    </Typography>
                                )}
                            </FileRowName>
                            {isMirrored && attachmentsNotOnBoard.has(String(attachment._id)) && (
                                <PushStateBox>
                                    <NotOnBoardChip />
                                    {canEdit && !isLocked && (
                                        <SendToBoard
                                            label="Send this file to the board again"
                                            busy={sendingToBoard === attachment._id}
                                            onClick={() => handleSendToBoard(attachment._id, { target: 'attachments', attachmentIds: [attachment._id] })}
                                        />
                                    )}
                                </PushStateBox>
                            )}
                            {canEdit && (
                                <Tooltip title="Remove attachment">
                                    <IconButton size="small" onClick={() => setConfirmDetachId(attachment._id)} aria-label="remove attachment">
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </FileRow>
                    ))}
                    {canEdit && (
                        <Box mt={2}>
                            <FilePondUploader
                                setAttachments={setPendingFiles}
                                onUploadStatusChange={setIsUploading}
                            />
                        </Box>
                    )}
                </Section>

                {!isCreating && item?._id && (
                    <Section>
                        <SectionLabel>Comments</SectionLabel>
                        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                            {isMirrored
                                ? 'Comments on a mirrored card belong to the connected board — treat them as visible to the client.'
                                : 'Comments on a board card are internal — clients never see them.'}
                        </Typography>
                        <CommentThread
                            parentType="card"
                            parentId={item._id}
                            comments={comments}
                            onChanged={(next) => { if (typeof onCardCommentsChanged === 'function') onCardCommentsChanged(item._id, next); }}
                            isLocked={isLocked}
                            lockPrivacyTo="Internal"
                            defaultPrivacy="Internal"
                            emptyText="No comments yet."
                            showPrivacyChip={!isMirrored}
                            onRetryPush={isMirrored ? (commentId) => handleSendToBoard(commentId, { target: 'comment', commentId }) : null}
                            retryingPushId={sendingToBoard}
                        />
                    </Section>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={isSaving}>Cancel</Button>
                <Button onClick={handlePrimary} variant="contained" disabled={primaryDisabled}>{primaryLabel}</Button>
            </DialogActions>

            <ConfirmDialog
                open={!!confirmDetachId}
                onClose={() => setConfirmDetachId(null)}
                onConfirm={() => handleDetach(confirmDetachId)}
                title="Remove attachment"
                message="This will permanently delete the attachment from this card. Continue?"
                confirmLabel="Remove"
                confirmColor="error"
            />

            <ImageViewer
                open={!!viewerAttachment}
                onClose={() => setViewerAttachment(null)}
                attachment={viewerAttachment}
                onDownload={handleDownloadFile}
                onReupload={handleAttachmentReupload}
            />
        </StyledDialog>
    );
};

export default KanbanCardDialog;
